import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Landmark, Loader2, CheckCircle2 } from "lucide-react";

const inputCls = "w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink";

function Section({ letter, title, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-hairline">
        <span className="w-7 h-7 rounded-full bg-ink text-paper flex items-center justify-center font-serif text-sm">{letter}</span>
        <h3 className="font-serif text-lg text-ink">{title}</h3>
      </div>
      <div className="grid md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function MembershipForm() {
  const [f, setF] = useState({
    title: "", name: "", ic: "", gender: "", marital: "", place_of_birth: "", ethnicity: "",
    address: "", correspondence_address: "", phone_office: "", mobile: "", email: "",
    nominee: { name: "", relationship: "", dob: "", ic: "", phone_home: "", mobile: "" },
    occupation: { category: "", field: "", employer_name: "", employer_address: "", position: "", phone: "", fax: "", academic: "", professional_body: "", how_heard: "" },
    about: { investment_estimate: "", other_coops: "", volunteer: "", expectations: "", contributions: "", interests: "", referrals: "" },
    payment: { registration_fee: "0", share_amount: "100", additional_share: "0" },
    proposers: ["", ""],
    declaration_agreed: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setN = (grp, k) => (e) => setF({ ...f, [grp]: { ...f[grp], [k]: e.target.value } });
  const total = (parseFloat(f.payment.registration_fee || 0) + parseFloat(f.payment.share_amount || 0) + parseFloat(f.payment.additional_share || 0)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.name.trim() || !f.ic.trim()) return setError("Nama & No. K.P wajib diisi / Name & IC are required");
    if (!f.declaration_agreed) return setError("Sila persetujui pengisytiharan / Please agree to the declaration");
    setSaving(true);
    try {
      await api.post("/public/membership-apply", { ...f, proposers: f.proposers.filter(Boolean).map((n) => ({ name: n })) });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) { setError(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (done) return (
    <Shell>
      <div className="text-center py-10" data-testid="apply-done">
        <CheckCircle2 size={52} className="text-positive mx-auto mb-4" />
        <h2 className="font-serif text-2xl text-ink">Permohonan Dihantar!</h2>
        <p className="text-sm text-slate mt-2">Terima kasih. Permohonan anda akan disemak & diluluskan oleh pihak Koperasi dalam tempoh 30 hari.<br />Your application has been submitted for approval.</p>
      </div>
    </Shell>
  );

  return (
    <Shell wide>
      <h1 className="font-serif text-2xl text-ink">Borang Permohonan Keanggotaan</h1>
      <p className="text-sm text-slate mb-1">Membership Application Form</p>
      <p className="text-xs text-slate mb-6">Saham Minima RM100 · Yuran Pendaftaran RM0 (Percuma)</p>

      {error && <div className="mb-5 bg-brick-bg text-brick border border-brick/20 px-3 py-2 rounded-sm text-sm" data-testid="apply-error">{error}</div>}

      <form onSubmit={submit}>
        <Section letter="A" title="Keterangan Diri / Personal Details">
          <Field label="Gelaran / Title"><input value={f.title} onChange={set("title")} data-testid="apply-title" className={inputCls} placeholder="En./Pn./Dr." /></Field>
          <Field label="Nama Penuh / Full Name *"><input value={f.name} onChange={set("name")} data-testid="apply-name" className={inputCls} /></Field>
          <Field label="No. K.P (Baru) / IC No *"><input value={f.ic} onChange={set("ic")} data-testid="apply-ic" className={inputCls} placeholder="900101-14-5533" /></Field>
          <Field label="Jantina / Gender">
            <select value={f.gender} onChange={set("gender")} data-testid="apply-gender" className={inputCls}><option value="">—</option><option>Lelaki</option><option>Perempuan</option></select>
          </Field>
          <Field label="Status / Marital">
            <select value={f.marital} onChange={set("marital")} className={inputCls}><option value="">—</option><option>Bujang</option><option>Berkahwin</option><option>Lain-lain</option></select>
          </Field>
          <Field label="Tempat Lahir / Place of Birth"><input value={f.place_of_birth} onChange={set("place_of_birth")} className={inputCls} /></Field>
          <Field label="Keturunan / Ethnicity"><input value={f.ethnicity} onChange={set("ethnicity")} className={inputCls} /></Field>
          <Field label="Emel / Email"><input type="email" value={f.email} onChange={set("email")} data-testid="apply-email" className={inputCls} /></Field>
          <Field label="Telefon (HP) / Mobile *"><input value={f.mobile} onChange={set("mobile")} data-testid="apply-mobile" className={inputCls} /></Field>
          <Field label="Telefon (Pej) / Office"><input value={f.phone_office} onChange={set("phone_office")} className={inputCls} /></Field>
          <Field label="Alamat / Address" full><input value={f.address} onChange={set("address")} className={inputCls} /></Field>
          <Field label="Alamat Surat-menyurat / Correspondence Address" full><input value={f.correspondence_address} onChange={set("correspondence_address")} className={inputCls} /></Field>
        </Section>

        <Section letter="A" title="Penama / Waris (Nominee)">
          <Field label="Nama Penama / Nominee Name"><input value={f.nominee.name} onChange={setN("nominee", "name")} className={inputCls} /></Field>
          <Field label="Hubungan / Relationship"><input value={f.nominee.relationship} onChange={setN("nominee", "relationship")} className={inputCls} /></Field>
          <Field label="No. K.P Penama / Nominee IC"><input value={f.nominee.ic} onChange={setN("nominee", "ic")} className={inputCls} /></Field>
          <Field label="Tarikh Lahir / DOB"><input type="date" value={f.nominee.dob} onChange={setN("nominee", "dob")} className={inputCls} /></Field>
          <Field label="Tel (Rumah) / Home"><input value={f.nominee.phone_home} onChange={setN("nominee", "phone_home")} className={inputCls} /></Field>
          <Field label="Tel (HP) / Mobile"><input value={f.nominee.mobile} onChange={setN("nominee", "mobile")} className={inputCls} /></Field>
        </Section>

        <Section letter="B" title="Butiran Pekerjaan / Employment">
          <Field label="Kategori Pekerjaan / Category">
            <select value={f.occupation.category} onChange={setN("occupation", "category")} className={inputCls}><option value="">—</option><option>Bekerja</option><option>Peniaga</option><option>Pelabur</option><option>Pengarah Syarikat</option><option>Pemilik Syarikat</option><option>Pesara</option><option>Lain-lain</option></select>
          </Field>
          <Field label="Bidang / Field">
            <select value={f.occupation.field} onChange={setN("occupation", "field")} className={inputCls}><option value="">—</option><option>Kewangan</option><option>Pelaburan</option><option>Perniagaan</option><option>Pendidikan</option><option>Jururunding</option><option>Perkhidmatan</option><option>Lain-lain</option></select>
          </Field>
          <Field label="Nama Majikan / Employer"><input value={f.occupation.employer_name} onChange={setN("occupation", "employer_name")} className={inputCls} /></Field>
          <Field label="Jawatan / Position"><input value={f.occupation.position} onChange={setN("occupation", "position")} className={inputCls} /></Field>
          <Field label="Alamat Majikan / Employer Address" full><input value={f.occupation.employer_address} onChange={setN("occupation", "employer_address")} className={inputCls} /></Field>
          <Field label="Kelulusan Akademik Tertinggi / Highest Academic"><input value={f.occupation.academic} onChange={setN("occupation", "academic")} className={inputCls} /></Field>
          <Field label="Keahlian Badan Ikhtisas / Professional Body"><input value={f.occupation.professional_body} onChange={setN("occupation", "professional_body")} className={inputCls} /></Field>
          <Field label="Bagaimana anda tahu tentang Koperasi? / How did you hear?">
            <select value={f.occupation.how_heard} onChange={setN("occupation", "how_heard")} className={inputCls}><option value="">—</option><option>Kenalan</option><option>Media Cetak</option><option>Buletin Komuniti</option><option>Penyelidikan</option><option>Media Elektronik</option><option>SKM</option><option>Internet</option><option>Lain-lain</option></select>
          </Field>
        </Section>

        <Section letter="C" title="Tentang Diri Anda / About You">
          <Field label="Anggaran jumlah pelaburan / Estimated investment">
            <select value={f.about.investment_estimate} onChange={setN("about", "investment_estimate")} className={inputCls}><option value="">—</option><option>RM1,000</option><option>RM1,001 - RM5,000</option><option>RM5,001 - RM10,000</option><option>Melebihi RM10,000</option></select>
          </Field>
          <Field label="Anggota koperasi lain? / Other coops"><input value={f.about.other_coops} onChange={setN("about", "other_coops")} className={inputCls} /></Field>
          <Field label="Pengalaman sukarela / Volunteer experience" full><textarea rows={2} value={f.about.volunteer} onChange={setN("about", "volunteer")} className={inputCls} /></Field>
          <Field label="Pengharapan dari Koperasi / Expectations" full><textarea rows={2} value={f.about.expectations} onChange={setN("about", "expectations")} className={inputCls} /></Field>
          <Field label="Sumbangan yang boleh diberi / Contributions" full><textarea rows={2} value={f.about.contributions} onChange={setN("about", "contributions")} className={inputCls} /></Field>
          <Field label="3 bidang pelaburan yang diminati / Investment interests" full><input value={f.about.interests} onChange={setN("about", "interests")} className={inputCls} placeholder="Contoh: Hartanah, Teknologi, Pertanian" /></Field>
        </Section>

        <Section letter="D" title="Bayaran / Payment">
          <Field label="Yuran Pendaftaran / Registration Fee (RM)"><input type="number" value={f.payment.registration_fee} onChange={setN("payment", "registration_fee")} className={inputCls} /></Field>
          <Field label="Jumlah Syer (Min RM100) / Share Amount (RM)"><input type="number" value={f.payment.share_amount} onChange={setN("payment", "share_amount")} data-testid="apply-share" className={inputCls} /></Field>
          <Field label="Tambahan Syer / Additional Share (RM)"><input type="number" value={f.payment.additional_share} onChange={setN("payment", "additional_share")} className={inputCls} /></Field>
          <Field label="JUMLAH / TOTAL (RM)"><div className="font-mono text-lg text-ink py-1.5">RM {total}</div></Field>
        </Section>

        <Section letter="E" title="Pengisytiharan / Declaration">
          <Field label="Pencadang 1 / Proposer 1"><input value={f.proposers[0]} onChange={(e) => setF({ ...f, proposers: [e.target.value, f.proposers[1]] })} className={inputCls} /></Field>
          <Field label="Pencadang 2 / Proposer 2"><input value={f.proposers[1]} onChange={(e) => setF({ ...f, proposers: [f.proposers[0], e.target.value] })} className={inputCls} /></Field>
          <div className="md:col-span-2 bg-paper border border-hairline rounded-sm p-4 text-xs text-slate leading-relaxed">
            Saya mengaku bahawa semua maklumat yang diberikan adalah benar dan tepat; akan terikat dengan Undang-undang Kecil Koperasi; bukan seorang bankrap yang belum dilepaskan; dan tidak pernah dibuang daripada mana-mana koperasi dalam tempoh setahun terakhir.
          </div>
          <label className="md:col-span-2 flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={f.declaration_agreed} onChange={(e) => setF({ ...f, declaration_agreed: e.target.checked })} data-testid="apply-declaration" className="mt-0.5 accent-brass w-4 h-4" />
            <span className="text-sm text-ink">Saya bersetuju dengan pengisytiharan di atas. / I agree to the declaration above.</span>
          </label>
        </Section>

        <button type="submit" disabled={saving} data-testid="apply-submit" className="w-full bg-brass text-ink font-semibold hover:bg-brass-hover py-3 rounded-sm text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
          {saving && <Loader2 size={16} className="animate-spin" />} Hantar Permohonan / Submit Application
        </button>
      </form>
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-ink py-8 px-4">
      <div className={`mx-auto ${wide ? "max-w-2xl" : "max-w-md"}`}>
        <div className="flex items-center justify-center gap-2 mb-5">
          <Landmark className="text-brass" strokeWidth={1.5} size={26} />
          <span className="font-serif text-xl text-paper">KKPKLB</span>
        </div>
        <div className="bg-white rounded-md shadow-2xl p-7">{children}</div>
        <p className="text-center text-paper/40 text-xs mt-4">Koperasi Kumpulan Profesional Kuala Lumpur Berhad</p>
      </div>
    </div>
  );
}
